<template>
  <div :class="{
      'animated fadeIn': true,
      card,
    }" v-if="entity">

    <div v-if="card" class="card-header">
      <slot name="header"></slot>
    </div>

    <div :class="{ 'card-body': card }">
      <form class="form-inline form-inputs" @submit.prevent="firstPageSearch">
        <div class="form-group col-sm-4 search">
          <input v-model="input.search"
            @input.prevent="callDebouncedFirstPageSearch"
            @blur.prevent="firstPageSearch"
            type="text"
            class="form-control"
            style="margin-bottom:0;width:100%;"
            name="search"
            id="search"
            ref="search"
            placeholder="Buscar"
            autofocus/>
        </div>
      </form>

      <hr>

      <table class="table table-sm table-hover table-striped col-sm-12" v-if="collection.length > 0">
        <thead>
          <tr>
            <th v-for="(field, index) in options.fields"
              @click="field.sortable ? sortCollection(field) : null"
              :key="'datatable-header-' + index"
              :class="[
                get(field, 'sortable', false) ? 'sortable' : null,
                get(field, 'action', false) ? 'action' : 'field',
                get(input, 'orderBy.name', null) === field.key ? 'active' : null,
                'text-' + get(field, 'align', 'left'),
                field.class || ''
              ]"
              :style="field.style || ''">

              {{ field.label || '' }}

              <i v-if="(field.sortable === true) && (get(input, 'orderBy.name', null) === field.key)"
                :class="`float-right fa fa-sort-alpha-${input.orderBy.direction}`"></i>
            </th>

            <th v-if="actions.length" class="text-center">
              Ações
            </th>
          </tr>
        </thead>
       <tbody>
          <tr v-for="(instance, index) in collection" :key="`row-${index}`">
            <td  v-for="(field, fieldIndex) in options.fields"
              :key="`cell-field-${index}-${fieldIndex}`"
              :style="field.style"
              :class="[
                'text-' + get(field, 'align', 'left'),
                field.class || ''
              ]">

              <template v-if="field.component">
                <component
                  :is="field.component"
                  :field="field"
                  :entity="entity"
                  :http="http"
                  :instance="instance"
                  v-bind="field.props || {}"></component>
              </template>

              <span v-else>
                <router-link :to="{ name: 'manageables.edit', params: {
                  entity: entity.key,
                  id: instance.id
                }}">
                  {{ applyFormatters(field, get(instance, field.key, '')) }}
                </router-link>
              </span>
            </td>

            <td v-if="actions.length" class="text-center">
              <template v-for="(action, actionIndex) in actions">
                <div v-if="action.component"
                  :key="`cell-action-${index}-${actionIndex}`"
                  style="display: inline-block">

                  <component :is="action.component"
                    :entity="entity"
                    :instance="instance"
                    :http="http"
                    @refresh="search">
                  </component>
                </div>

                <span v-else :key="`cell-action-${index}-${actionIndex}`">
                  --
                </span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <template v-else>
        <div v-if="!processing">
          Nenhum registro <span v-if="entity.name">de {{entity.name}} </span>foi encontrado. Verifique os dados de pequisa<span v-if="entity.add !== false">
            &nbsp;ou
            <router-link :to="{ name: 'manageables.create', params: { entity: entity.key }}">
              clique aqui para criar um novo
            </router-link>
          </span>.
        </div>

        <div v-else>
          Buscando ...
        </div>
      </template>

      <hr>

      <div class="row">
        <div class="col-lg-4">
          <multiselect v-model="input.per_page"
            style="width: 100px; display: inline-block"
            @input="firstPageSearch"
            placeholder="Quantidade por página"
            :allowEmpty="false"
            :showLabels="false"
            :options="per_pageOptions"/>

            <span> Itens por página</span>
        </div>

        <div class="col">
          <div class="pagination-module" v-if="pagination.total > 0">
            <div class="align-left">
              Página <b>{{ pagination.current_page }}</b> de <b>{{ pagination.last_page }}</b>,&nbsp;
              <b>{{ pagination.from }}</b> até <b>{{ pagination.to }}</b> de <b>{{ pagination.total }}</b> registros
            </div>

            <div class="align-left">
              <b-pagination v-model="input.page"
                @change="search"
                :total-rows="pagination.total"
                :per-page="pagination.per_page"
                :limit="3"
                :hide-ellipsis="true"
                align="right" >
              </b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { get, set, clone, merge, debounce } from 'lodash'
import EditElement from './DatatableElements/EditElement'
import DeleteElement from './DatatableElements/DeleteElement'

export default {
  props: {
    entity: {
      type: Object,
      required: true
    },

    http: {
      type: Object,
      required: true
    },

    filter: {
      type: Object,
      default () { return {} }
    },

    card: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      processing: false,

      collection: [],

      pagination: {},
      emptyPagination: {
        current_page: 1,
        total: 0,
        last_page: null,
        from: 1,
        to: 1,
        per_page: 10
      },

      per_pageOptions: [10, 25, 50],

      input: {},
      emptyInput: {
        page: 1,
        per_page: 10,
        search: '',
        orderBy: { name: 'created_at', direction: 'desc' }
      },

      defaultActions: [
        { component: EditElement },
        { component: DeleteElement }
      ]
    }
  },

  watch: {
    entity (prev, next) {
      const nextEntity = get(next, 'key', null)
      const previousEntity = get(prev, 'key', null)

      if (nextEntity !== previousEntity) {
        this.pagination = clone(this.emptyPagination)
        this.input = clone(this.emptyInput)

        this.firstPageSearch()
      }
    }
  },

  computed: {
    options () { return this.entity.tableOptions || { fields: [], actions: [] } },

    actions () { return get(this.entity, 'tableOptions.actions', this.defaultActions) }
  },

  methods: {
    get,

    firstPageSearch () {
      this.input.page = this.pagination.current_page = 1

      this.search()
    },

    callDebouncedFirstPageSearch () {
      this.debouncedFirstPageSearch(this)
    },

    search () {
      const self = this

      self.$nextTick(() => {
        self.processing = true
        self.$Progress.start()
        let search = [self.input.search, self.filter]

        self.http
          .index({ params: Object.assign(merge(clone(self.input), { search: search }), {
            with: self.options.with || [],
            orderBy: self.options.orderBy || self.input.orderBy
          })})
          .then((res, body) => {
            self.$Progress.finish()
            self.processing = false
            self.collection = get(res.data, 'collection', [])
            self.pagination = get(res.data, 'pagination', clone(self.emptyPagination))

            self.pagination.per_page = parseInt(self.pagination.per_page, 10)
          })
          .catch((error) => {
            self.$Progress.fail()
            self.error = error
            self.processing = false
            self.collection = []
            self.pagination = clone(self.emptyPagination)

            return null
          })
      })
    },

    debouncedFirstPageSearch: debounce((context) => {
      context.input.page = context.pagination.current_page = 1

      context.search()
    }, 1000, { 'maxWait': 8000 }),

    sortCollection (field) {
      const self = this

      // If the current column is the same sorting
      if (get(self.input, 'orderBy.name', null) === field.key) {
        set(
          self.input,
          'orderBy.direction',
          get(self.input, 'orderBy.direction', 'desc') === 'asc' ? 'desc' : 'asc'
        )
      } else {
        set(self.input, 'orderBy.name', field.key)
        set(self.input, 'orderBy.direction', 'asc')
      }

      self.firstPageSearch()
    },

    applyFormatters (field, value) {
      let result = value

      if (field.formatters) {
        for (let i = 0; i < field.formatters.length; i++) {
          switch (field.formatters[i]['type']) {
            case 'date':
              result = moment(result, 'YYYY-MM-DD HH:mm::ss').format(field.formatters[i]['args'])
          }
        }
      }

      return result
    }

    // actExport: function (event) {
    //   let translateParams = this.getAllRefsValues('translate')
    //   translateParams._onepage = true
    //   this.http.index({
    //     params: translateParams
    //   }).then((res, body) => {
    //     let data = map(res.data.collection, partialRight(pick, entities[this.$route.params.entity].options.export))
    //     exportFromJSON({ data, fileName: this.$route.params.entity, exportType: 'xls' })
    //   }).catch((error) => {
    //     this.error = error
    //   })
    // }
  },

  created () {
    this.pagination = clone(this.emptyPagination)
    this.input = clone(this.emptyInput)

    this.firstPageSearch()
  }
}
</script>

<style lang="sass">

table
  thead
    th
      &.sortable
        cursor: pointer !important

        &.active
          background-color: #333

</style>



// WEBPACK FOOTER //
// src/components/ui/Datatable.vue